import '../styles.scss';

import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { Avatar, Button, Typography } from 'antd';
import { IoCaretDown, IoClose } from 'react-icons/io5';

import MangoSelectionModal from '../../../components/MangoSelectionModal/MangoSelectionModal';
import { useAppSelector } from '../../../shared/hooks';
import { IMango } from '../../../types/feedTypes';
import { Onboarding } from '../../../types/userTypes';
import NoDataFound from '../../Feed/components/NoDataFound/NoDataFound';
import TagSelectionModal from '../../Feed/Posts/PostItem/TagSelectionModal';

interface Props {
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
  selectedMangoes: IMango[];
  setSelectedMangoes: React.Dispatch<React.SetStateAction<IMango[]>>;
  profilePicUrl?: string;
  creatorName?: string;
  customTitle?: string;
  customDescription?: string;
  canEditSubscriberPost?: boolean;
  selectedExcludeMangoes: IMango[];
  setSelectedExcludeMangoes: React.Dispatch<React.SetStateAction<IMango[]>>;
}

export interface CreatePostHeaderRef {
  openTagModal: () => void;
}

const CreatePostHeader: React.ForwardRefRenderFunction<
  CreatePostHeaderRef,
  Props
> = (
  {
    selectedTags,
    setSelectedTags,
    selectedMangoes,
    setSelectedMangoes,
    profilePicUrl,
    creatorName,
    customTitle,
    customDescription,
    canEditSubscriberPost,
    selectedExcludeMangoes,
    setSelectedExcludeMangoes,
  },
  ref,
) => {
  const {
    hostMetadata: {
      offeringTitle,
      offeringTitles,
      creator,
      communityEnabledMangoes,
    },
    tags: appTags,
  } = useAppSelector((state) => state.app);
  const { subscribedMangoes, ...userDetails } = useAppSelector(
    (state) => state.user,
  );

  const { creatorMangoes, creatorFreeMangoes } = useAppSelector(
    (state) => state.mango,
  );

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isExclusiveMangoModalOpen, setIsExclusiveMangoModalOpen] =
    useState(false);
  const [isMangoModalOpen, setIsMangoModalOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openTagModal: () => {
      setIsTagModalOpen(true);
    },
  }));

  const tagItem = (key: string, value: string) => (
    <div
      onClick={() => {
        setSelectedTags((prev) => prev.filter((x) => x !== key));
      }}
      key={key}
      className={`post-tag-item ${key}`}>
      <Typography.Text className="post-tag-item-text">{value}</Typography.Text>

      <IoClose size={14} />
    </div>
  );

  const mangoesToSelectFrom = useMemo(() => {
    if (
      ['creator_completed', 'dual', 'creator_restricted'].includes(
        userDetails.type || '',
      )
    ) {
      return [...creatorMangoes, ...creatorFreeMangoes];
    } else if (
      subscribedMangoes.length > 0 &&
      communityEnabledMangoes &&
      communityEnabledMangoes.length > 0 &&
      ['fan_completed'].includes(userDetails.type || '')
    ) {
      const subscribedMangoesWithCommunityEnabled = subscribedMangoes.filter(
        (mango) => communityEnabledMangoes.includes(mango._id),
      );
      return subscribedMangoesWithCommunityEnabled;
    }
    return [];
  }, [
    creatorMangoes,
    creatorFreeMangoes,
    subscribedMangoes,
    userDetails.type,
    communityEnabledMangoes,
  ]);

  const excludeMangoList = useMemo(() => {
    let mangoes = [];

    if (userDetails.type === Onboarding.FAN_COMPLETED) {
      const communityEnabledMangoesList = communityEnabledMangoes || [];
      mangoes = [...(subscribedMangoes || [])].filter(
        (mango) =>
          communityEnabledMangoesList?.includes(mango._id) &&
          (selectedMangoes || []).every((each) => each?._id !== mango?._id),
      );
    } else {
      mangoes = [...creatorMangoes, ...creatorFreeMangoes].filter((mango) =>
        (selectedMangoes || []).every((each) => each?._id !== mango?._id),
      );
    }

    return mangoes || [];
  }, [
    userDetails.type,
    communityEnabledMangoes,
    subscribedMangoes,
    selectedMangoes,
    creatorMangoes,
    creatorFreeMangoes,
  ]);

  return (
    <>
      {selectedTags.length > 0 && (
        <div className="create-post-tags">
          {selectedTags.map((tag) => tagItem(tag, appTags[tag]?.value || ''))}
        </div>
      )}
      <div className="create-post-header">
        <Avatar
          size={55}
          src={
            profilePicUrl ||
            (userDetails.type === 'creator_restricted'
              ? creator.profilePicUrl
              : userDetails.profilePic)
          }
        />
        <div className="create-post-header-details">
          <Typography.Title level={5}>
            {creatorName ||
              (userDetails.type === 'creator_restricted'
                ? creator?.name || ''
                : userDetails.name)}
          </Typography.Title>
          <div className="create-post-header-details-mango">
            <Button
              className="siteBtn create-post-header-details-mango-button"
              onClick={() => setIsMangoModalOpen(true)}
              disabled={canEditSubscriberPost}
              style={{
                opacity: canEditSubscriberPost ? 0.5 : 1,
              }}>
              {selectedMangoes.length > 0 ? (
                <Typography.Text>
                  {selectedMangoes?.[0]?.title?.length > 15
                    ? `${selectedMangoes?.[0]?.title.substring(0, 12)}...`
                    : selectedMangoes?.[0]?.title}{' '}
                  {selectedMangoes.length > 1
                    ? `+${selectedMangoes.length - 1}`
                    : ''}
                </Typography.Text>
              ) : (
                <Typography.Text>
                  + {offeringTitle.toTitleCase()}
                </Typography.Text>
              )}
              <IoCaretDown size={14} />
            </Button>
            {userDetails?.type === Onboarding.CREATOR_COMPLETED ||
            userDetails?.type === Onboarding.CREATOR_RESTRICTED ||
            userDetails?.type === Onboarding.DUAL ? (
              <Button
                className="siteBtn create-post-header-details-mango-button"
                onClick={() => setIsExclusiveMangoModalOpen(true)}
                disabled={canEditSubscriberPost}
                style={{
                  opacity: canEditSubscriberPost ? 0.5 : 1,
                }}>
                <Typography.Text>
                  {selectedExcludeMangoes.length > 0 ? (
                    <Typography.Text>
                      {selectedExcludeMangoes?.[0]?.title?.length > 15
                        ? `${selectedExcludeMangoes?.[0]?.title.substring(
                            0,
                            12,
                          )}...`
                        : selectedExcludeMangoes?.[0]?.title}{' '}
                      {selectedExcludeMangoes.length > 1
                        ? `+${selectedExcludeMangoes.length - 1}`
                        : ''}
                    </Typography.Text>
                  ) : (
                    <Typography.Text>
                      + Exclude {offeringTitle.toTitleCase()}
                    </Typography.Text>
                  )}
                </Typography.Text>
                <IoCaretDown size={14} />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <TagSelectionModal
        selectedTags={selectedTags}
        show={isTagModalOpen}
        closeModal={() => setIsTagModalOpen(false)}
        handleSave={(tags) => {
          setSelectedTags(tags);
          setIsTagModalOpen(false);
        }}
      />
      {(isExclusiveMangoModalOpen || isMangoModalOpen) && (
        <MangoSelectionModal
          mangoes={
            isExclusiveMangoModalOpen ? excludeMangoList : mangoesToSelectFrom
          }
          selectedMangoes={
            isExclusiveMangoModalOpen ? selectedExcludeMangoes : selectedMangoes
          }
          handleSave={(m) => {
            if (isExclusiveMangoModalOpen) {
              setSelectedExcludeMangoes(m as IMango[]);
            } else {
              setSelectedMangoes(m as IMango[]);
            }
            setIsMangoModalOpen(false);
            setIsExclusiveMangoModalOpen(false);
          }}
          customTitle={
            isExclusiveMangoModalOpen
              ? `Select Exclude ${offeringTitle}`
              : customTitle
          }
          customHeaderTitle={
            isExclusiveMangoModalOpen
              ? `Select Exclude ${offeringTitle}`
              : customTitle
          }
          customDescription={
            isExclusiveMangoModalOpen
              ? `User who have access of this ${offeringTitles}, cant able to see this post`
              : customDescription
          }
          show={isMangoModalOpen || isExclusiveMangoModalOpen}
          closeModal={() => {
            setIsMangoModalOpen(false);
            setIsExclusiveMangoModalOpen(false);
          }}
          showSelectAllAndClearSection
          noMangoComponent={
            <NoDataFound
              title={`No ${offeringTitles} found`}
              subtitle={
                userDetails.type === 'fan_completed'
                  ? `You have not subscribed to any ${offeringTitles} yet. Subscribe to one to start posting content.`
                  : `You have not created any ${offeringTitles} yet. Create one to start creating content.`
              }
            />
          }
        />
      )}
    </>
  );
};

export default forwardRef(CreatePostHeader);
